import React, { useState, useEffect } from 'react';
import { teamsAPI, matchesAPI } from '../services/api';
import { FaHome, FaUsers, FaFutbol, FaNewspaper, FaCalendar, FaFileAlt, FaCog, FaChartBar, FaTrophy, FaUserTie, FaTv, FaYoutube, FaPlay } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import StandingsSection from '../components/admin/StandingsSection';
import DesignationsSection from '../components/admin/DesignationsSection';
import SettingsSection from '../components/admin/SettingsSection';

const AdminDashboard = () => {
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [news, setNews] = useState([]);
  const [communiques, setCommuniques] = useState([]);
  const [activeSection, setActiveSection] = useState('overview');
  const [formData, setFormData] = useState({});
  const { t } = useLanguage();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const teamsRes = await teamsAPI.getAll();
      const matchesRes = await matchesAPI.getAll();
      setTeams(teamsRes.data);
      setMatches(matchesRes.data);
      // Simuler des données pour news et communiqués
      setNews([
        { id: 1, title: 'Début de la saison 2024/2025', content: 'La nouvelle saison commence...', date: '2024-09-01' },
        { id: 2, title: 'Nouveau règlement', content: 'Les nouvelles règles...', date: '2024-08-15' }
      ]);
      setCommuniques([
        { id: 1, title: 'Communiqué officiel N°1', content: 'Informations importantes...', date: '2024-09-10' }
      ]);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('city', formData.city || '');
      data.append('stadium', formData.stadium || '');
      if (formData.logo) data.append('logo', formData.logo);

      await teamsAPI.create(data);
      loadData();
      setFormData({});
      alert('Équipe ajoutée avec succès!');
    } catch (error) {
      alert('Erreur lors de l\'ajout de l\'équipe');
    }
  };

  const handleMatchSubmit = async (e) => {
    e.preventDefault();
    try {
      await matchesAPI.create({
        ...formData,
        youtube_url: formData.youtube_url || ''
      });
      loadData();
      setFormData({});
      alert('Match créé avec succès!');
    } catch (error) {
      alert('Erreur lors de la création du match');
    }
  };

  const updateMatchScore = async (id, home_score, away_score) => {
    try {
      await matchesAPI.update(id, { home_score, away_score, status: 'finished' });
      loadData();
    } catch (error) {
      alert('Erreur lors de la mise à jour du score');
    }
  };

  const deleteTeam = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette équipe?')) {
      try {
        await teamsAPI.delete(id);
        loadData();
        alert('Équipe supprimée avec succès!');
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const editTeam = (team) => {
    setFormData({
      id: team.id,
      name: team.name,
      city: team.city,
      stadium: team.stadium,
      isEditing: true
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTeamUpdate = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('city', formData.city || '');
      data.append('stadium', formData.stadium || '');
      if (formData.logo) data.append('logo', formData.logo);

      await teamsAPI.update(formData.id, data);
      loadData();
      setFormData({});
      alert('Équipe modifiée avec succès!');
    } catch (error) {
      alert('Erreur lors de la modification de l\'équipe');
    }
  };

  const deleteMatch = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce match?')) {
      try {
        await matchesAPI.delete(id);
        loadData();
        alert('Match supprimé avec succès!');
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const editMatch = (match) => {
    setFormData({
      id: match.id,
      home_team_id: match.home_team_id,
      away_team_id: match.away_team_id,
      match_date: match.match_date ? match.match_date.slice(0, 16) : '',
      round: match.round,
      home_score: match.home_score,
      away_score: match.away_score,
      status: match.status,
      youtube_url: match.youtube_url || '',
      isEditing: true
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMatchUpdate = async (e) => {
    e.preventDefault();
    try {
      await matchesAPI.update(formData.id, {
        home_team_id: formData.home_team_id,
        away_team_id: formData.away_team_id,
        match_date: formData.match_date,
        round: formData.round,
        home_score: formData.home_score || 0,
        away_score: formData.away_score || 0,
        status: formData.status || 'upcoming',
        youtube_url: formData.youtube_url || ''
      });
      loadData();
      setFormData({});
      alert('Match modifié avec succès!');
    } catch (error) {
      alert('Erreur lors de la modification du match');
    }
  };

  const cancelEdit = () => {
    setFormData({});
  };

  const menuItems = [
    { id: 'overview', icon: FaHome, label: 'Vue d\'ensemble', labelAr: 'نظرة عامة' },
    { id: 'standings', icon: FaTrophy, label: 'Classement & Points', labelAr: 'الترتيب والنقاط' },
    { id: 'teams', icon: FaUsers, label: 'Gestion des équipes', labelAr: 'إدارة الفرق' },
    { id: 'matches', icon: FaFutbol, label: 'Gestion des matchs', labelAr: 'إدارة المباريات' },
    { id: 'live_management', icon: FaTv, label: 'Espace Live', labelAr: 'البث المباشر' },
    { id: 'designations', icon: FaUserTie, label: 'Désignations', labelAr: 'التعيينات' },
    { id: 'news', icon: FaNewspaper, label: 'Actualités', labelAr: 'الأخبار' },
    { id: 'calendar', icon: FaCalendar, label: 'Calendrier', labelAr: 'الرزنامة' },
    { id: 'communiques', icon: FaFileAlt, label: 'Communiqués', labelAr: 'القوانين واللوائح' },
    { id: 'stats', icon: FaChartBar, label: 'Statistiques', labelAr: 'الإحصائيات' },
    { id: 'settings', icon: FaCog, label: 'Paramètres', labelAr: 'الإعدادات' }
  ];

  return (
    <div style={styles.container}>
      {/* Sidebar Verticale */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <h2 style={styles.sidebarTitle}>Admin Panel</h2>
          <p style={{ fontSize: '12px', color: '#999', margin: '5px 0 0 0' }}>v2.0</p>
        </div>
        <nav style={styles.sidebarNav}>
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              style={{
                ...styles.menuItem,
                ...(activeSection === item.id ? styles.menuItemActive : {})
              }}
            >
              <item.icon style={styles.menuIcon} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Overview */}
        {activeSection === 'overview' && (
          <div style={styles.section}>
            <h1 style={styles.pageTitle}>Tableau de bord</h1>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <FaUsers style={styles.statIcon} />
                <h3 style={styles.statNumber}>{teams.length}</h3>
                <p style={styles.statLabel}>Équipes</p>
              </div>
              <div style={styles.statCard}>
                <FaFutbol style={styles.statIcon} />
                <h3 style={styles.statNumber}>{matches.length}</h3>
                <p style={styles.statLabel}>Matchs</p>
              </div>
              <div style={styles.statCard}>
                <FaChartBar style={styles.statIcon} />
                <h3 style={styles.statNumber}>{matches.filter(m => m.status === 'finished').length}</h3>
                <p style={styles.statLabel}>Matchs terminés</p>
              </div>
              <div style={styles.statCard}>
                <FaCalendar style={styles.statIcon} />
                <h3 style={styles.statNumber}>{matches.filter(m => m.status === 'upcoming').length}</h3>
                <p style={styles.statLabel}>Matchs à venir</p>
              </div>
            </div>
          </div>
        )}

        {/* Standings & Points */}
        {activeSection === 'standings' && <StandingsSection />}

        {/* Designations */}
        {activeSection === 'designations' && <DesignationsSection />}

        {/* Live Management Section */}
        {activeSection === 'live_management' && (
          <div style={styles.section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h1 style={styles.pageTitle}>Espace Direct Live</h1>
              <div style={{ background: '#ff0000', color: 'white', padding: '5px 15px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', animation: 'pulse 1.5s infinite' }}>
                EN DIRECT
              </div>
            </div>

            <div style={styles.tableCard}>
              <h2 style={styles.cardTitle}>Contrôle des flux en direct</h2>
              <div style={{ display: 'grid', gap: '20px' }}>
                {matches.sort((a, b) => new Date(b.match_date) - new Date(a.match_date)).slice(0, 10).map(match => (
                  <div key={match.id} style={{ ...styles.matchRow, background: match.status === 'live' ? '#fff5f5' : '#fff', borderLeft: match.status === 'live' ? '5px solid #ff0000' : '5px solid #eee' }}>
                    <div style={{ display: 'flex', flex: 1, alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{match.home_team_name} vs {match.away_team_name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{new Date(match.match_date).toLocaleString('fr-FR')}</div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <select
                          value={match.status}
                          onChange={async (e) => {
                            try {
                              await matchesAPI.update(match.id, { ...match, status: e.target.value });
                              loadData();
                              alert('Statut mis à jour !');
                            } catch (error) { alert('Erreur'); }
                          }}
                          style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px' }}
                        >
                          <option value="upcoming">À venir</option>
                          <option value="live">En direct</option>
                          <option value="finished">Terminé</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                        <FaYoutube color="#ff0000" />
                        <input
                          type="text"
                          placeholder="ID YouTube (ex: dQw4w9WgXcQ)"
                          defaultValue={match.youtube_url || ''}
                          onBlur={async (e) => {
                            if (e.target.value !== (match.youtube_url || '')) {
                              try {
                                await matchesAPI.update(match.id, { ...match, youtube_url: e.target.value });
                                loadData();
                                alert('Lien YouTube mis à jour !');
                              } catch (error) { alert('Erreur'); }
                            }
                          }}
                          style={{ ...styles.input, margin: 0, flex: 1 }}
                        />
                      </div>

                      {match.youtube_url && match.status === 'live' && (
                        <a
                          href={`http://localhost:3000/live`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ background: '#000', color: '#fff', padding: '8px 15px', borderRadius: '5px', textDecoration: 'none', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}
                        >
                          <FaPlay size={10} /> Voir le flux
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '20px', padding: '20px', background: '#e3f2fd', borderRadius: '10px', color: '#0d47a1', fontSize: '14px' }}>
              <strong>💡 Conseil :</strong> Pour activer un match en direct, passez son statut à <strong>"En direct"</strong> et renseignez l'ID YouTube. Les changements sont visibles immédiatement sur la page Live publique.
            </div>
          </div>
        )}

        {/* Teams Management */}
        {activeSection === 'teams' && (
          <div style={styles.section}>
            <h1 style={styles.pageTitle}>Gestion des équipes</h1>

            <div style={styles.formCard}>
              <h2 style={styles.cardTitle}>
                {formData.isEditing ? 'Modifier une équipe' : 'Ajouter une équipe'}
              </h2>
              <form onSubmit={formData.isEditing ? handleTeamUpdate : handleTeamSubmit} style={styles.form}>
                <input
                  type="text"
                  placeholder="Nom de l'équipe"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={styles.input}
                  required
                />
                <input
                  type="text"
                  placeholder="Ville"
                  value={formData.city || ''}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  style={styles.input}
                />
                <input
                  type="text"
                  placeholder="Stade"
                  value={formData.stadium || ''}
                  onChange={(e) => setFormData({ ...formData, stadium: e.target.value })}
                  style={styles.input}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({ ...formData, logo: e.target.files[0] })}
                  style={styles.input}
                />
                <div style={styles.formActions}>
                  <button type="submit" style={styles.submitBtn}>
                    {formData.isEditing ? 'Modifier l\'équipe' : 'Ajouter l\'équipe'}
                  </button>
                  {formData.isEditing && (
                    <button type="button" onClick={cancelEdit} style={styles.cancelBtn}>
                      Annuler
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div style={styles.tableCard}>
              <h2 style={styles.cardTitle}>Liste des équipes ({teams.length})</h2>
              <div style={styles.teamsGrid}>
                {teams.map(team => (
                  <div key={team.id} style={styles.teamCard}>
                    {team.logo_url ? (
                      <img
                        src={team.logo_url}
                        alt={team.name}
                        style={styles.teamLogo}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div style={styles.teamLogoPlaceholder}>
                        {team.name.charAt(0)}
                      </div>
                    )}
                    <h3 style={styles.teamName}>{team.name}</h3>
                    <p style={styles.teamInfo}>{team.city}</p>
                    <p style={styles.teamInfo}>{team.stadium}</p>
                    {team.group_number && (
                      <p style={styles.teamGroup}>Groupe {team.group_number}</p>
                    )}
                    <div style={styles.cardActions}>
                      <button
                        onClick={() => editTeam(team)}
                        style={styles.editBtn}
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => deleteTeam(team.id)}
                        style={styles.deleteBtn}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Matches Management */}
        {activeSection === 'matches' && (
          <div style={styles.section}>
            <h1 style={styles.pageTitle}>Gestion des matchs</h1>

            <div style={styles.formCard}>
              <h2 style={styles.cardTitle}>
                {formData.isEditing ? 'Modifier un match' : 'Créer un match'}
              </h2>
              <form onSubmit={formData.isEditing ? handleMatchUpdate : handleMatchSubmit} style={styles.form}>
                <select
                  value={formData.home_team_id || ''}
                  onChange={(e) => setFormData({ ...formData, home_team_id: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="">Sélectionner équipe domicile</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
                <select
                  value={formData.away_team_id || ''}
                  onChange={(e) => setFormData({ ...formData, away_team_id: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="">Sélectionner équipe extérieur</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
                <input
                  type="datetime-local"
                  value={formData.match_date || ''}
                  onChange={(e) => setFormData({ ...formData, match_date: e.target.value })}
                  style={styles.input}
                  required
                />
                <input
                  type="number"
                  placeholder="Numéro de journée"
                  value={formData.round || ''}
                  onChange={(e) => setFormData({ ...formData, round: e.target.value })}
                  style={styles.input}
                />
                <input
                  type="text"
                  placeholder="ID Vidéo YouTube (ex: dQw4w9WgXcQ)"
                  value={formData.youtube_url || ''}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  style={styles.input}
                />
                {formData.isEditing && (
                  <>
                    <input
                      type="number"
                      placeholder="Score domicile"
                      value={formData.home_score || 0}
                      onChange={(e) => setFormData({ ...formData, home_score: e.target.value })}
                      style={styles.input}
                      min="0"
                    />
                    <input
                      type="number"
                      placeholder="Score extérieur"
                      value={formData.away_score || 0}
                      onChange={(e) => setFormData({ ...formData, away_score: e.target.value })}
                      style={styles.input}
                      min="0"
                    />
                    <select
                      value={formData.status || 'upcoming'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      style={styles.input}
                    >
                      <option value="upcoming">À venir</option>
                      <option value="live">En direct</option>
                      <option value="finished">Terminé</option>
                    </select>
                  </>
                )}
                <div style={styles.formActions}>
                  <button type="submit" style={styles.submitBtn}>
                    {formData.isEditing ? 'Modifier le match' : 'Créer le match'}
                  </button>
                  {formData.isEditing && (
                    <button type="button" onClick={cancelEdit} style={styles.cancelBtn}>
                      Annuler
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div style={styles.tableCard}>
              <h2 style={styles.cardTitle}>Liste des matchs ({matches.length})</h2>
              {matches.map(match => (
                <div key={match.id} style={styles.matchRow}>
                  <div style={styles.matchInfo}>
                    <span style={styles.matchTeam}>{match.home_team_name}</span>
                    <span style={styles.matchScore}>
                      {match.home_score || 0} - {match.away_score || 0}
                    </span>
                    <span style={styles.matchTeam}>{match.away_team_name}</span>
                    <span style={styles.matchDate}>
                      {new Date(match.match_date).toLocaleDateString('fr-FR')}
                    </span>
                    <span style={{
                      ...styles.matchStatus,
                      background: match.status === 'finished' ? '#28a745' :
                        match.status === 'live' ? '#dc3545' : '#ffc107',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      {match.status === 'finished' ? 'Terminé' :
                        match.status === 'live' ? 'En direct' : 'À venir'}
                    </span>
                    {match.youtube_url && (
                      <span style={{ fontSize: '12px', color: '#ff0000', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FaYoutube /> {match.youtube_url}
                      </span>
                    )}
                  </div>
                  <div style={styles.matchActions}>
                    <button
                      onClick={() => editMatch(match)}
                      style={styles.editBtn}
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => deleteMatch(match.id)}
                      style={styles.deleteBtn}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Other sections - Coming soon */}
        {activeSection === 'news' && (
          <div style={styles.section}>
            <h1 style={styles.pageTitle}>Gestion des Actualités</h1>

            <div style={styles.formCard}>
              <h2 style={styles.cardTitle}>Ajouter une actualité</h2>
              <form onSubmit={(e) => { e.preventDefault(); alert('Fonctionnalité bientôt disponible'); }} style={styles.form}>
                <input
                  type="text"
                  placeholder="Titre de l'actualité"
                  style={styles.input}
                  required
                />
                <textarea
                  placeholder="Contenu de l'actualité"
                  style={{ ...styles.input, minHeight: '150px' }}
                  required
                />
                <input
                  type="file"
                  accept="image/*"
                  style={styles.input}
                />
                <button type="submit" style={styles.submitBtn}>Publier l'actualité</button>
              </form>
            </div>

            <div style={styles.tableCard}>
              <h2 style={styles.cardTitle}>Liste des actualités ({news.length})</h2>
              {news.map(item => (
                <div key={item.id} style={styles.newsCard}>
                  <h3 style={styles.newsTitle}>{item.title}</h3>
                  <p style={styles.newsDate}>{new Date(item.date).toLocaleDateString('fr-FR')}</p>
                  <p style={styles.newsContent}>{item.content}</p>
                  <div style={styles.newsActions}>
                    <button style={styles.editBtn}>Modifier</button>
                    <button style={styles.deleteBtn}>Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'communiques' && (
          <div style={styles.section}>
            <h1 style={styles.pageTitle}>Gestion des Communiqués</h1>

            <div style={styles.formCard}>
              <h2 style={styles.cardTitle}>Ajouter un communiqué</h2>
              <form onSubmit={(e) => { e.preventDefault(); alert('Fonctionnalité bientôt disponible'); }} style={styles.form}>
                <input
                  type="text"
                  placeholder="Titre du communiqué"
                  style={styles.input}
                  required
                />
                <textarea
                  placeholder="Contenu du communiqué"
                  style={{ ...styles.input, minHeight: '200px' }}
                  required
                />
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={styles.input}
                />
                <button type="submit" style={styles.submitBtn}>Publier le communiqué</button>
              </form>
            </div>

            <div style={styles.tableCard}>
              <h2 style={styles.cardTitle}>Liste des communiqués ({communiques.length})</h2>
              {communiques.map(item => (
                <div key={item.id} style={styles.newsCard}>
                  <h3 style={styles.newsTitle}>{item.title}</h3>
                  <p style={styles.newsDate}>{new Date(item.date).toLocaleDateString('fr-FR')}</p>
                  <p style={styles.newsContent}>{item.content}</p>
                  <div style={styles.newsActions}>
                    <button style={styles.editBtn}>Modifier</button>
                    <button style={styles.deleteBtn}>Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'calendar' && (
          <div style={styles.section}>
            <h1 style={styles.pageTitle}>Gestion du Calendrier</h1>
            <div style={styles.formCard}>
              <h2 style={styles.cardTitle}>Configuration du calendrier</h2>
              <p>Gérez les dates importantes de la saison:</p>
              <ul style={styles.calendarList}>
                <li>Début de saison: 01/09/2024</li>
                <li>Fin de saison: 30/06/2025</li>
                <li>Pause hivernale: 20/12/2024 - 10/01/2025</li>
              </ul>
              <button style={styles.submitBtn}>Modifier les dates</button>
            </div>
          </div>
        )}

        {activeSection === 'stats' && (
          <div style={styles.section}>
            <h1 style={styles.pageTitle}>Statistiques Avancées</h1>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <h3 style={styles.statNumber}>{teams.length}</h3>
                <p style={styles.statLabel}>Équipes totales</p>
              </div>
              <div style={styles.statCard}>
                <h3 style={styles.statNumber}>{matches.length}</h3>
                <p style={styles.statLabel}>Matchs programmés</p>
              </div>
              <div style={styles.statCard}>
                <h3 style={styles.statNumber}>
                  {matches.filter(m => m.status === 'finished').reduce((sum, m) => sum + m.home_score + m.away_score, 0)}
                </h3>
                <p style={styles.statLabel}>Buts marqués</p>
              </div>
              <div style={styles.statCard}>
                <h3 style={styles.statNumber}>{news.length}</h3>
                <p style={styles.statLabel}>Actualités publiées</p>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'settings' && (
          <SettingsSection />
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    background: '#f5f5f5'
  },
  sidebar: {
    width: '280px',
    background: 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)',
    color: 'white',
    position: 'fixed',
    height: '100vh',
    overflowY: 'auto',
    boxShadow: '2px 0 10px rgba(0,0,0,0.1)'
  },
  sidebarHeader: {
    padding: '30px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  sidebarTitle: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#c41e3a'
  },
  sidebarNav: {
    padding: '20px 0'
  },
  menuItem: {
    width: '100%',
    padding: '15px 20px',
    background: 'transparent',
    border: 'none',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    fontSize: '15px',
    textAlign: 'left'
  },
  menuItemActive: {
    background: '#c41e3a',
    borderLeft: '4px solid white'
  },
  menuIcon: {
    fontSize: '18px'
  },
  mainContent: {
    marginLeft: '280px',
    flex: 1,
    padding: '30px'
  },
  section: {
    maxWidth: '1200px'
  },
  pageTitle: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '30px'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  },
  statCard: {
    background: 'white',
    padding: '30px',
    borderRadius: '15px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  statIcon: {
    fontSize: '40px',
    color: '#c41e3a',
    marginBottom: '15px'
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#333',
    margin: '10px 0'
  },
  statLabel: {
    fontSize: '16px',
    color: '#666'
  },
  formCard: {
    background: 'white',
    padding: '30px',
    borderRadius: '15px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    marginBottom: '30px'
  },
  tableCard: {
    background: 'white',
    padding: '30px',
    borderRadius: '15px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '20px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  input: {
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '15px',
    transition: 'border 0.3s'
  },
  submitBtn: {
    padding: '15px',
    background: '#c41e3a',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background 0.3s'
  },
  teamsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '20px'
  },
  teamCard: {
    background: '#f9f9f9',
    padding: '20px',
    borderRadius: '10px',
    textAlign: 'center',
    border: '2px solid #e0e0e0'
  },
  teamLogo: {
    width: '80px',
    height: '80px',
    objectFit: 'contain',
    marginBottom: '15px'
  },
  teamName: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '5px'
  },
  teamInfo: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '5px'
  },
  teamGroup: {
    fontSize: '13px',
    color: '#c41e3a',
    fontWeight: 'bold',
    marginTop: '5px',
    padding: '4px 8px',
    background: 'rgba(196, 30, 58, 0.1)',
    borderRadius: '4px',
    display: 'inline-block'
  },
  teamLogoPlaceholder: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: '#c41e3a',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: 'bold',
    margin: '0 auto 15px'
  },
  cardActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '15px',
    justifyContent: 'center'
  },
  formActions: {
    display: 'flex',
    gap: '10px'
  },
  deleteBtn: {
    padding: '8px 15px',
    background: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'background 0.3s'
  },
  cancelBtn: {
    padding: '15px',
    background: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background 0.3s'
  },
  matchRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px',
    background: '#f9f9f9',
    borderRadius: '8px',
    marginBottom: '10px',
    border: '2px solid #e0e0e0'
  },
  matchInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    flex: 1,
    flexWrap: 'wrap'
  },
  matchActions: {
    display: 'flex',
    gap: '10px'
  },
  matchTeam: {
    fontSize: '15px',
    fontWeight: '600',
    minWidth: '120px'
  },
  matchScore: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#c41e3a',
    minWidth: '60px',
    textAlign: 'center'
  },
  matchDate: {
    fontSize: '13px',
    color: '#666'
  },
  matchStatus: {
    fontSize: '12px',
    fontWeight: '600'
  },
  scoreInput: {
    width: '60px',
    padding: '8px',
    textAlign: 'center',
    border: '2px solid #ddd',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  matchVs: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#666'
  },
  comingSoon: {
    background: 'white',
    padding: '60px',
    borderRadius: '15px',
    textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  newsCard: {
    background: '#f9f9f9',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '15px',
    border: '2px solid #e0e0e0'
  },
  newsTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#333'
  },
  newsDate: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '10px'
  },
  newsContent: {
    fontSize: '14px',
    color: '#555',
    marginBottom: '15px',
    lineHeight: '1.6'
  },
  newsActions: {
    display: 'flex',
    gap: '10px'
  },
  editBtn: {
    padding: '8px 15px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  calendarList: {
    listStyle: 'none',
    padding: '20px 0',
    fontSize: '16px',
    lineHeight: '2'
  }
};

export default AdminDashboard;
